import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Portfolio.css';

const Portfolio = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const mobileApps = [
    {
      title: 'Light of Hope',
      arabicTitle: 'ضوء الامل',
      englishTitle: 'daw\' alamil',
      // description: 'Long-term maintenance, layout fixes, and production stability.',
      description: 'A spiritual and educational app providing hope and guidance. Available in both Arabic and English.',
      technologies: ['iOS', 'Android', 'Java', 'Objective-C'],
      mainIcon: 'fas fa-heart',
      links: [
        { name: 'App Store', url: 'https://itunes.apple.com/us/app/dw-alaml/id541529988?mt=8', icon: 'fab fa-apple' },
        { name: 'Play Store', url: 'https://play.google.com/store/apps/details?id=com.telco.daw2_amal', icon: 'fab fa-google-play' }
      ]
    },
    {
      title: 'Ways of Peace',
      arabicTitle: 'سبل السلام',
      englishTitle: 'subul alsalam',
      description: 'An app focused on peace and spiritual guidance, helping users find inner tranquility.',
      technologies: ['iOS', 'Android', 'Java', 'Objective-C'],
      mainIcon: 'fas fa-dove',
      links: [
        { name: 'App Store', url: 'https://itunes.apple.com/us/app/%D8%B3%D8%A8%D9%84-%D8%A7%D9%84%D8%B3%D9%84%D8%A7%D9%85/id720085467?mt=8', icon: 'fab fa-apple' },
        { name: 'Play Store', url: 'https://play.google.com/store/apps/details?id=com.dijlah.soblalsalam', icon: 'fab fa-google-play' }
      ]
    },
    {
      title: 'Cognitive Nuggets',
      arabicTitle: 'شذرات معرفية',
      englishTitle: 'shadharat maerifia',
      description: 'Educational app providing knowledge and cognitive development content.',
      technologies: ['iOS', 'Android', 'Java', 'Objective-C'],
      mainIcon: 'fas fa-brain',
      links: [
        { name: 'App Store', url: 'https://apps.apple.com/us/app/%D8%B4%D8%B0%D8%B1%D8%A7%D8%AA-%D9%85%D8%B9%D8%B1%D9%81%D9%8A%D8%A9/id1499208611', icon: 'fab fa-apple' },
        { name: 'Play Store', url: 'https://play.google.com/store/apps/details?id=com.yaqoobi.mobadarat&hl=ar', icon: 'fab fa-google-play' }
      ]
    },
    {
      title: 'Guide for Leader and Pilgrim',
      arabicTitle: 'دليل المرشد والحاج',
      englishTitle: 'dalil almurshid walhaji',
      description: 'Comprehensive guide app for religious guides and pilgrims, providing essential information and support.',
      technologies: ['iOS', 'Android', 'Java', 'Objective-C'],
      mainIcon: 'fas fa-map-marked-alt',
      links: [
        { name: 'App Store', url: 'https://itunes.apple.com/us/developer/ahmed-mohammed/id1126791990', icon: 'fab fa-apple' },
        { name: 'Play Store', url: 'https://play.google.com/store/apps/details?id=com.telco.alhaj', icon: 'fab fa-google-play' }
      ]
    }
  ];

  const otherProjects = [
    {
      title: 'Groupe DNK',
      description: 'Bilingual business website for a Montreal engineering firm: services, projects, team, and contact. Live at groupednk.ca.',
      technologies: ['React', 'JavaScript', 'CSS3', 'Node.js'],
      icon: 'fas fa-globe',
      url: 'https://www.groupednk.ca/',
      linkLabel: 'Visit groupednk.ca'
    },
    {
      title: 'Mobile VAS Applications',
      description: 'Comprehensive iOS and Android applications for telecom value-added services including billing systems, customer management, and network monitoring tools. Developed with focus on scalability and real-time data processing.',
      technologies: ['Java', 'Objective-C', 'Android', 'iOS'],
      icon: 'fas fa-mobile-alt'
    },
    {
      title: 'Financial Platform Modernization',
      description: 'Complete legacy system modernization project converting outdated financial platforms to modern .NET architecture with SQL Server database. Improved performance, security, and user experience while maintaining data integrity.',
      technologies: ['.NET', 'SQL Server', 'C#', 'ASP.NET'],
      icon: 'fas fa-cogs'
    }
  ];

  return (
    <section id="portfolio" className="portfolio" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Our Work</h2>
          <p>Client websites and applications delivered for real businesses</p>
        </motion.div>

        {/* Mobile Development Section */}
        <motion.div 
          id="mobile-development"
          className="portfolio-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="section-subheader">
            <h3>Mobile Application Development</h3>
            <h4>Real production apps</h4>
            <p>Rescue, conversion, long-term maintenance, layout fixes, and production stability</p>
          </div>
          
          <motion.div 
            className="portfolio-grid"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {mobileApps.map((item, index) => (
              <motion.div
                key={index}
                className="portfolio-item mobile-app-item"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.4 + index * 0.1,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
              >
                <div className="portfolio-image">
                  <div className="mobile-card-content-frame">
                    <div className="mobile-app-info">
                      <h3 className="mobile-app-title">{item.title}</h3>
                      <div className="mobile-app-names-bottom">
                        <span className="mobile-english-name">{item.englishTitle}</span>
                        <span className="mobile-arabic-name">{item.arabicTitle}</span>
                      </div>
                      {item.description && (
                        <div className={`mobile-app-description-bottom ${item.description.length > 80 ? 'long-description' : ''}`}>
                          <p className="mobile-app-description">{item.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mobile-app-icons-sidebar">
                    <div className="app-main-icon">
                      <i className={item.mainIcon}></i>
                    </div>
                    <a 
                      href={item.links[1].url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="store-icon android"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="fab fa-google-play"></i>
                    </a>
                    <a 
                      href={item.links[0].url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="store-icon apple"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="fab fa-apple"></i>
                    </a>
                  </div>
                  <motion.div
                    className="portfolio-overlay"
                    initial={{ y: '100%' }}
                    whileHover={{ y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>{item.title}</h3>
                    <div className="app-names-overlay">
                      <span className="arabic-name">{item.arabicTitle}</span>
                      <span className="english-name">{item.englishTitle}</span>
                    </div>
                    <p className="portfolio-description">{item.description}</p>
                    {item.sub_description && (
                      <p className="portfolio-sub-description">{item.sub_description}</p>
                    )}
                    <div className="portfolio-tech">
                      {item.technologies.map((tech, techIndex) => (
                        <span key={techIndex}>{tech}</span>
                      ))}
                    </div>
                    <div className="portfolio-links">
                      {item.links.map((link, linkIndex) => (
                        <motion.a
                          key={linkIndex}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="portfolio-link"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <i className={link.icon}></i>
                          <span>{link.name}</span>
                        </motion.a>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Other Development Projects Section */}
        <motion.div 
          id="other-development"
          className="portfolio-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="section-subheader">
            <h3>Web & Other Development Projects</h3>
            <p>Business websites, system modernization, and custom software solutions</p>
          </div>
          
          <motion.div 
            className="portfolio-grid"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {otherProjects.map((item, index) => (
              <motion.div
                key={index}
                className="portfolio-item"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.6, 
                  delay: 0.5 + index * 0.2,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
              >
                <div className="portfolio-image">
                  <div className="app-main-icon-top-left">
                    <i className={item.icon}></i>
                  </div>
                  <div className="app-info">
                    <h3 className="app-title">{item.title}</h3>
                    <p className="app-description">{item.description}</p>
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="portfolio-site-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="fas fa-external-link-alt"></i>
                        {item.linkLabel || 'Visit website'}
                      </a>
                    )}
                  </div>
                  <motion.div
                    className="portfolio-overlay"
                    initial={{ y: '100%' }}
                    whileHover={{ y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <div className="portfolio-tech">
                      {item.technologies.map((tech, techIndex) => (
                        <span key={techIndex}>{tech}</span>
                      ))}
                    </div>
                    {item.url && (
                      <div className="portfolio-links">
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="portfolio-link"
                        >
                          <i className="fas fa-external-link-alt"></i>
                          <span>{item.linkLabel || 'Visit website'}</span>
                        </a>
                      </div>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;


